<template>
	<div class="home-page" @click="closeUserInfo">
		<div class="app-container" :class="{ fullscreen: isFullscreen }">
			<div class="navi-bar">
				<div class="navi-bar-box">
					<div class="top">
						<div class="user-head-image">
							<head-image :name="userStore.userInfo.nickName" :size="38"
								:url="userStore.userInfo.headImageThumb" @click.native="showSettingDialog = true">
							</head-image>
						</div>
						<div class="menu">
							<el-tooltip class="custom-tooltip" effect="dark" content="消息" placement="right-end" :open-delay="3000" :offset="10">
								<router-link class="link" v-bind:to="'/home/chat'">
									<div class="menu-item">
										<i class="el-icon-chat-dot-round icon"></i>
										<span class="label">消息</span>
										<div v-show="unreadCount > 0" class="unread-text">{{ unreadCount }}</div>
									</div>
								</router-link>
							</el-tooltip>
							<el-tooltip class="custom-tooltip" effect="dark" content="通讯录" placement="right-end" :open-delay="3000" :offset="10">
								<router-link class="link" v-bind:to="'/home/contact'">
									<div class="menu-item">
										<span class="icon iconfont icon-friend nav-outline-icon"></span>
										<span class="label">通讯录</span>
										<!-- 通讯录：沿用好友申请未读数量的小红点 -->
										<div v-show="friendRequestCount > 0" class="unread-text">{{ formatBadgeCount(friendRequestCount) }}</div>
									</div>
								</router-link>
							</el-tooltip>
							<el-tooltip v-if="oaWorkbenchVisible" class="custom-tooltip" effect="dark" content="工作台" placement="right-end" :open-delay="3000" :offset="10">
								<router-link class="link" v-bind:to="'/home/oa'">
									<div class="menu-item">
										<span class="icon iconfont icon-receipt" style="color:#000;"></span>
										<span class="label">工作台</span>
									</div>
								</router-link>
							</el-tooltip>
						</div>
					</div>

					<div class="botoom">
						<el-tooltip class="custom-tooltip" effect="dark" content="全屏" placement="right-end" :open-delay="3000" :offset="10">
							<div class="bottom-item" @click="isFullscreen = !isFullscreen">
								<i class="el-icon-full-screen"></i>
								<span class="label">&nbsp;&nbsp;全屏</span>
							</div>
						</el-tooltip>
						<el-tooltip class="custom-tooltip" effect="dark" content="收藏" placement="right-end" :open-delay="3000" :offset="10">
							<div class="bottom-item" @click="openFavoriteCenter">
								<svg class="favorite-cube-outline-icon" viewBox="0 0 24 24" aria-hidden="true">
									<path d="M12 2.5 20.5 7.2 20.5 16.8 12 21.5 3.5 16.8 3.5 7.2Z"></path>
									<path d="M3.8 7.4 12 12 20.2 7.4M12 12V21"></path>
								</svg>
								<span class="label">&nbsp;&nbsp;收藏</span>
							</div>
						</el-tooltip>
						<el-tooltip class="custom-tooltip" effect="dark" content="设置" placement="right-end" :open-delay="3000" :offset="10">
							<div class="bottom-item" @click="openAccountSettings">
								<i class="el-icon-setting"></i>
								<span class="label">&nbsp;&nbsp;设置</span>
							</div>
						</el-tooltip>
						<el-tooltip class="custom-tooltip" effect="dark" content="我的" placement="right-end" :open-delay="3000" :offset="10">
							<div class="bottom-item" @click="showSetting">
								<i class="el-icon-user"></i>
								<span class="label">&nbsp;&nbsp;我的</span>
							</div>
						</el-tooltip>
						<el-tooltip class="custom-tooltip" effect="dark" content="退出" placement="right-end" :open-delay="3000" :offset="10">
							<div class="bottom-item bottom-exit" @click="onExit()">
								<span class="icon iconfont icon-exit"></span>
								<span class="label">退出</span>
							</div>
						</el-tooltip>
					</div>
				</div>
			</div>
			<div class="content-box">
				<router-view></router-view>
			</div>
			<setting :visible="showSettingDialog" @close="closeSetting()"></setting>
			<account-settings :visible="showAccountSettingsDialog" @close="closeAccountSettings()"></account-settings>
			<favorite-panel :visible="showFavoriteCenter" mode="list" @close="showFavoriteCenter = false"></favorite-panel>
			<user-info ref="userInfo"></user-info>
			<full-image ref="fullImage"></full-image>
			<rtc-private-video ref="rtcPrivateVideo"></rtc-private-video>
			<rtc-group-video ref="rtcGroupVideo"></rtc-group-video>
			<rtc-group-join ref="rtcGroupJoin"></rtc-group-join>
		</div>
	</div>
</template>

<script>
import HeadImage from '../components/common/HeadImage.vue';
import Setting from '../components/setting/Setting.vue';
import AccountSettings from '../components/setting/AccountSettings.vue';
import UserInfo from '../components/common/UserInfo.vue';
import FullImage from '../components/common/FullImage.vue';
import FavoritePanel from '../components/favorite/FavoritePanel.vue';
import RtcPrivateVideo from '../components/rtc/RtcPrivateVideo.vue';
import RtcPrivateAcceptor from '../components/rtc/RtcPrivateAcceptor.vue';
import RtcGroupVideo from '../components/rtc/RtcGroupVideo.vue';
import RtcGroupJoin from '../components/rtc/RtcGroupJoin.vue';
import { getPendingFriendRequestCount } from '../utils/friendRequest';
import { fetchOaWorkbenchStatus } from '../api/oa';
import {
	DEFAULT_MESSAGE_SOUND_KEY,
	MESSAGE_SOUND_STORAGE_KEY,
	getMessageSoundOption
} from '@/config/messageSoundOptions';

const appRoot = process.env.BASE_URL || '/';

export default {
	components: {
		HeadImage,
		Setting,
		AccountSettings,
		UserInfo,
		FullImage,
		FavoritePanel,
		RtcPrivateVideo,
		RtcPrivateAcceptor,
		RtcGroupVideo,
		RtcGroupJoin
	},
	data() {
		return {
			showSettingDialog: false,
			lastPlayAudioTime: new Date().getTime() - 1000,
			isFullscreen: true,
			reconnecting: false,
			friendRequestCount: 0,
			friendRequestCountUpdatedAt: 0,
			isCheckingFriendRequests: false,
			friendRequestRecheckRequested: false,
			friendRequestCheckPromise: null,
			unreadCount: 0,
			pendingRtcPrivateMessages: [],
			oaWorkbenchVisible: false,
			showFavoriteCenter: false,
			showAccountSettingsDialog: false
		}
	},
	methods: {
		openFavoriteCenter() {
			this.showFavoriteCenter = true;
		},
		openAccountSettings() {
			this.showAccountSettingsDialog = true;
		},
		closeAccountSettings() {
			this.showAccountSettingsDialog = false;
		},
		dispatchRtcPrivateMessage(msg) {
			const rtcRef = this.$refs.rtcPrivateVideo;
			if (rtcRef && typeof rtcRef.onRTCMessage === 'function') {
				rtcRef.onRTCMessage(msg);
				return;
			}
			this.pendingRtcPrivateMessages.push(msg);
			this.$nextTick(() => this.flushPendingRtcPrivateMessages());
		},
		flushPendingRtcPrivateMessages() {
			const rtcRef = this.$refs.rtcPrivateVideo;
			if (!rtcRef || typeof rtcRef.onRTCMessage !== 'function' || !this.pendingRtcPrivateMessages.length) {
				return;
			}
			const queued = [...this.pendingRtcPrivateMessages];
			this.pendingRtcPrivateMessages = [];
			queued.forEach((msg) => rtcRef.onRTCMessage(msg));
		},
		init() {
			this.$eventBus.$on('openPrivateVideo', (rctInfo) => {
				// 进入单人视频通话
				this.$refs.rtcPrivateVideo.open(rctInfo);
			});
			this.$eventBus.$on('openGroupVideo', (rctInfo) => {
				// 进入多人视频通话
				this.$refs.rtcGroupVideo.open(rctInfo);
			});
			this.$eventBus.$on('openUserInfo', (user, pos) => {
				// 打开用户卡片
				this.$refs.userInfo.open(user, pos);
			});
			this.$eventBus.$on('openFullImage', url => {
				// 图片全屏
				this.$refs.fullImage.open(url);
			});
			
			// 监听好友申请数量更新事件
			this.$eventBus.$on('updateFriendRequestCount', (count) => {
				console.log(`Home.vue 收到好友申请数量更新事件: ${count}`);
				this.friendRequestCount = count;
				this.friendRequestCountUpdatedAt = Date.now();
				sessionStorage.setItem('friendRequestCount', count);
			});
			this.loadStore().then(() => {
				// ws初始化
				this.$wsApi.connect(process.env.VUE_APP_WS_URL, sessionStorage.getItem("accessToken"));
				this.$wsApi.onConnect(() => {
					if (this.reconnecting) {
						this.onReconnectWs();
					} else {
						// 加载离线消息
						this.pullPrivateOfflineMessage(this.chatStore.privateMsgMaxId);
						this.pullGroupOfflineMessage(this.chatStore.groupMsgMaxId);
					}
				});
				this.$wsApi.onMessage((cmd, msgInfo) => {
					if (cmd == 2) {
						// 关闭ws
						this.$wsApi.close(3000)
						// 异地登录，强制下线
						this.$alert("您已在其他地方登录，将被强制下线", "强制下线通知", {
							confirmButtonText: '确定',
							callback: action => {
								location.href = appRoot;
							}
						});

					} else if (cmd == 3) {
						// 插入私聊消息
            //console.log("7777777777777777");
						this.handlePrivateMessage(msgInfo);
					} else if (cmd == 4) {
						// 插入群聊消息
						this.handleGroupMessage(msgInfo);
					} else if (cmd == 5) {
						// 处理系统消息
						this.handleSystemMessage(msgInfo);
					} else if (cmd == 6) {
						// 处理好友申请消息
						this.handleFriendRequestMessage(msgInfo);
					}
				});
				this.$wsApi.onClose((e) => {
					if (e.code != 3000) {
						// 断线重连
						this.reconnectWs();
					}
				});
			}).catch((e) => {
				console.log("初始化失败", e);
			})
		},
		reconnectWs() {
			// 记录标志
			this.reconnecting = true;
			// 重新加载一次个人信息，目的是为了保证网络已经正常且token有效
			this.userStore.loadUser().then(() => {
				// 断线重连
				this.$message.error("连接断开，正在尝试重新连接...");
				this.$wsApi.reconnect(process.env.VUE_APP_WS_URL, sessionStorage.getItem(
					"accessToken"));
			}).catch(() => {
				// 10s后重试
				setTimeout(() => this.reconnectWs(), 10000)
			})
		},
		onReconnectWs() {
			// 重连成功
			this.reconnecting = false;
			// 重新加载群和好友
			const promises = [];
			promises.push(this.friendStore.loadFriend());
			promises.push(this.groupStore.loadGroup());
			Promise.all(promises).then(() => {
				this.chatStore.syncChatsFromFriends(this.friendStore.friends);
				// 加载离线消息
				this.pullPrivateOfflineMessage(this.chatStore.privateMsgMaxId);
				this.pullGroupOfflineMessage(this.chatStore.groupMsgMaxId);
				this.$message.success("重新连接成功");
				this.checkFriendRequests();
			}).catch(() => {
				this.$message.error("初始化失败");
				this.onExit();
			})
		},
		
		// 向uniapp发送消息
		sendMessageToUniApp(messageType, messageData) {
			console.log('=== Home.vue 向uniapp发送消息 ===');
			console.log('消息类型:', messageType);
			console.log('消息数据:', messageData);
			
			// 通过事件总线发送消息
			this.$eventBus.$emit('SEND_TO_UNIAPP', {
				type: messageType,
				data: messageData
			});
			
			// 也可以通过其他方式发送，比如WebSocket
			// 这里我们使用事件总线，让uniapp端监听
		},
		loadStore() {
			return this.userStore.loadUser().then(() => {
				const promises = [];
				promises.push(this.friendStore.loadFriend());
				promises.push(this.groupStore.loadGroup());
				promises.push(this.chatStore.loadChat());
				promises.push(this.configStore.loadConfig());
				promises.push(this.loadOaWorkbenchStatus());
				return Promise.all(promises).then(() => {
					this.chatStore.syncChatsFromFriends(this.friendStore.friends);
				});
			})
		},
		async loadOaWorkbenchStatus() {
			try {
				const body = await fetchOaWorkbenchStatus();
				const status = body.data || {};
				const visible = body.code === 200 && !!status.oaEnabled && status.sysUserStatus === '0';
				this.oaWorkbenchVisible = visible;
				if (!visible && this.$route.path.indexOf('/home/oa') === 0) {
					this.$router.replace('/home/chat');
				}
			} catch (error) {
				console.warn('[OA工作台] 加载状态失败:', error);
				this.oaWorkbenchVisible = false;
				if (this.$route.path.indexOf('/home/oa') === 0) {
					this.$router.replace('/home/chat');
				}
			}
		},
		unloadStore() {
			this.friendStore.clear();
			this.groupStore.clear();
			this.chatStore.clear();
			this.userStore.clear();
		},
		pullPrivateOfflineMessage(minId) {
			this.chatStore.setLoadingPrivateMsg(true)
			this.$http({
				url: "/message/private/pullOfflineMessage?minId=" + minId,
				method: 'GET'
			}).catch(() => {
				this.chatStore.setLoadingPrivateMsg(false)
			})
		},
		pullGroupOfflineMessage(minId) {
			this.chatStore.setLoadingGroupMsg(true)
			this.$http({
				url: "/message/group/pullOfflineMessage?minId=" + minId,
				method: 'GET'
			}).catch(() => {
				this.chatStore.setLoadingGroupMsg(false)
			})
		},
		handlePrivateMessage(msg) {
			// 标记这条消息是不是自己发的
			msg.selfSend = msg.sendId == this.userStore.userInfo.id;
			// 好友id
			let friendId = msg.selfSend ? msg.recvId : msg.sendId;
			// 会话信息
			let chatInfo = {
				type: 'PRIVATE',
				targetId: friendId
			}
			// 消息加载标志
			if (msg.type == this.$enums.MESSAGE_TYPE.LOADING) {
				this.chatStore.setLoadingPrivateMsg(JSON.parse(msg.content))
				return;
			}
			// 消息已读处理，清空已读数量
			if (msg.type == this.$enums.MESSAGE_TYPE.READED) {
				this.chatStore.resetUnreadCount(chatInfo)
				return;
			}
			// 消息回执处理,改消息状态为已读
			if (msg.type == this.$enums.MESSAGE_TYPE.RECEIPT) {
				this.chatStore.readedMessage({
					friendId: msg.sendId
				})
				return;
			}
			// 消息撤回
			if (msg.type == this.$enums.MESSAGE_TYPE.RECALL) {
				this.chatStore.recallMessage(msg, chatInfo)
				return;
			}
			// 新增好友
			if (msg.type == this.$enums.MESSAGE_TYPE.FRIEND_NEW) {
				this.friendStore.addFriend(JSON.parse(msg.content));
				return;
			}
			// 删除好友
			if (msg.type == this.$enums.MESSAGE_TYPE.FRIEND_DEL) {
				this.friendStore.removeFriend(friendId);
				return;
			}
			// 单人webrtc 信令
			if (this.$msgType.isRtcPrivate(msg.type)) {
				this.dispatchRtcPrivateMessage(msg)
				return;
			}
			// 插入消息
			if (this.$msgType.isNormal(msg.type) || this.$msgType.isTip(msg.type) || this.$msgType.isAction(msg.type)) {
				let friend = this.loadFriendInfo(friendId);
				this.insertPrivateMessage(friend, msg);
			}
		},
		insertPrivateMessage(friend, msg) {
			let chatInfo = {
				type: 'PRIVATE',
				targetId: friend.id,
				showName: friend.nickName,
				headImage: friend.headImage
			};
			// 打开会话
			this.chatStore.openChat(chatInfo);
			// 插入消息
			this.chatStore.insertMessage(msg, chatInfo);
			// 播放提示音
			if (!msg.selfSend && this.$msgType.isNormal(msg.type) &&
				msg.status != this.$enums.MESSAGE_STATUS.READED) {
				this.playAudioTip(chatInfo);
			}
		},
		handleGroupMessage(msg) {
			// 标记这条消息是不是自己发的
			msg.selfSend = msg.sendId == this.userStore.userInfo.id;
			let chatInfo = {
				type: 'GROUP',
				targetId: msg.groupId
			}
			// 消息加载标志
			if (msg.type == this.$enums.MESSAGE_TYPE.LOADING) {
				this.chatStore.setLoadingGroupMsg(JSON.parse(msg.content))
				return;
			}
			// 消息已读处理
			if (msg.type == this.$enums.MESSAGE_TYPE.READED) {
				// 我已读对方的消息，清空已读数量
				this.chatStore.resetUnreadCount(chatInfo)
				return;
			}
			// 消息回执处理
				if (msg.type == this.$enums.MESSAGE_TYPE.RECEIPT) {
					// 更新消息已读人数
					let msgInfo = {
						id: msg.id,
						groupId: msg.groupId,
						receiptTotal: msg.receiptTotal,
						readedCount: msg.readedCount,
						unreadCount: msg.unreadCount,
						receiptOk: msg.receiptOk
					};
					this.chatStore.updateMessage(msgInfo, chatInfo)
					return;
			}
			// 消息撤回
			if (msg.type == this.$enums.MESSAGE_TYPE.RECALL) {
				this.chatStore.recallMessage(msg, chatInfo)
				return;
			}
			// 新增群
			if (msg.type == this.$enums.MESSAGE_TYPE.GROUP_NEW) {
				this.groupStore.addGroup(JSON.parse(msg.content));
				return;
			}
			// 删除群
			if (msg.type == this.$enums.MESSAGE_TYPE.GROUP_DEL) {
				// 检查群聊是否真的被解散了（通过查询群聊信息）
				// 如果群聊还存在，说明只是消息被删除，不删除群聊条目，只删除消息会话
				this.$http({
					url: `/group/find/${msg.groupId}`,
					method: 'GET'
				}).then((group) => {
					// 群聊存在且未解散，说明只是消息被删除，只删除消息会话，不删除群聊条目
					if (group && !group.dissolve) {
						this.chatStore.removeGroupChat(msg.groupId);
					} else {
						// 群聊真的被解散了，删除群聊条目和消息会话
						this.groupStore.removeGroup(msg.groupId);
						this.chatStore.removeGroupChat(msg.groupId);
					}
				}).catch((error) => {
					// 如果查询失败（群聊不存在），说明群聊被解散了，删除群聊条目和消息会话
					console.log('查询群聊信息失败，可能是群聊已解散:', error);
					this.groupStore.removeGroup(msg.groupId);
					this.chatStore.removeGroupChat(msg.groupId);
				});
				return;
			}
			// 群聊邀请消息
			if (msg.type == this.$enums.MESSAGE_TYPE.RTC_GROUP_INVITE) {
				console.log('收到群聊邀请消息:', msg);
				try {
					const inviteData = JSON.parse(msg.content);
					console.log('解析的邀请数据:', inviteData);

					// 检查当前用户是否是发起者
					if (inviteData.host && inviteData.host.id === this.userStore.userInfo.id) {
						console.log('当前用户是发起者，跳过邀请对话框');
						return;
					}

					// 只有非发起者才显示邀请对话框
					this.$refs.rtcGroupJoin.open(inviteData);
				} catch (error) {
					console.error('处理群聊邀请失败:', error);
				}
				return;
			}
				// 群视频信令
				if (this.$msgType.isRtcGroup(msg.type)) {
					console.log('收到群聊WebRTC消息:', msg.type, msg);
					if (this.$refs.rtcGroupJoin && this.$refs.rtcGroupJoin.isShow &&
						typeof this.$refs.rtcGroupJoin.onRTCMessage === 'function') {
						console.log('转发WebRTC消息到RtcGroupJoin组件');
						this.$nextTick(() => {
							this.$refs.rtcGroupJoin.onRTCMessage(msg);
						});
					}
					// 检查RtcGroupVideo组件是否已打开
					if (this.$refs.rtcGroupVideo && this.$refs.rtcGroupVideo.isShow) {
						console.log('转发WebRTC消息到RtcGroupVideo组件');
						this.$nextTick(() => {
							this.$refs.rtcGroupVideo.onRTCMessage(msg);
					});
				} else {
					console.log('RtcGroupVideo组件未打开，忽略WebRTC消息:', msg.type);
				}
				return;
			}
			// 插入群聊消息
			if (this.$msgType.isNormal(msg.type) || this.$msgType.isTip(msg.type) || this.$msgType.isAction(msg.type)) {
				let group = this.loadGroupInfo(msg.groupId);
				this.insertGroupMessage(group, msg);
			}
		},
		insertGroupMessage(group, msg) {
			let chatInfo = {
				type: 'GROUP',
				targetId: group.id,
				showName: group.showGroupName,
				headImage: group.headImageThumb
			};
			// 打开会话
			this.chatStore.openChat(chatInfo);
			// 插入消息
			this.chatStore.insertMessage(msg, chatInfo);
			// 播放提示音
			if (!msg.selfSend && msg.type <= this.$enums.MESSAGE_TYPE.VIDEO &&
				msg.status != this.$enums.MESSAGE_STATUS.READED) {
				this.playAudioTip(chatInfo);
			}
		},
		handleSystemMessage(msg) {
			// 用户被封禁
			if (msg.type == this.$enums.MESSAGE_TYPE.USER_BANNED) {
				this.$wsApi.close(3000);
				this.$alert("您的账号已被管理员封禁,原因:" + msg.content, "账号被封禁", {
					confirmButtonText: '确定',
					callback: () => {
						this.onExit();
					}
				});
				return;
			}
			if (msg.type == this.$enums.MESSAGE_TYPE.FRIEND_REQUEST_CHANGED) {
				this.handleFriendRequestMessage(msg);
				return;
			}
		},
		
		// 处理好友申请消息
		handleFriendRequestMessage(msg) {
			console.log("收到好友申请消息:", msg);
			
			if (!msg || msg.content === 'NEW') {
				this.$message({
					message: '收到新的好友申请',
					type: 'info',
					duration: 3000
				});
				this.playAudioTip();
			}
			
			// 立即检查一次好友申请
			this.checkFriendRequests();
			this.$eventBus.$emit('friendRequestChanged');
		},
		
		// 检查好友申请
		checkFriendRequests() {
			if (this.isCheckingFriendRequests) {
				this.friendRequestRecheckRequested = true;
				return this.friendRequestCheckPromise || Promise.resolve();
			}
			
			const requestStartedAt = Date.now();
			this.isCheckingFriendRequests = true;
			this.friendRequestRecheckRequested = false;
			
			this.friendRequestCheckPromise = this.$http({
				url: "/friend-request/received",
				method: "GET",
				silent: true
			}).then((data) => {
				// 过滤出待处理的好友请求
				const count = getPendingFriendRequestCount(data);
				
				console.log(`检查好友申请完成，待处理数量: ${count}`);
				if (requestStartedAt < this.friendRequestCountUpdatedAt) {
					console.log('跳过旧的好友申请数量检查结果');
					return;
				}
				
				// 更新好友申请数量
				this.updateFriendRequestCount(count);
				
			}).catch((error) => {
				console.error('检查好友申请失败:', error);
			}).finally(() => {
				this.isCheckingFriendRequests = false;
				this.friendRequestCheckPromise = null;
				if (this.friendRequestRecheckRequested) {
					this.friendRequestRecheckRequested = false;
					return this.checkFriendRequests();
				}
			});
			return this.friendRequestCheckPromise;
		},
		
		// 更新好友申请数量
		updateFriendRequestCount(count) {
			this.friendRequestCount = count;
			this.friendRequestCountUpdatedAt = Date.now();
			// 存储到本地
			sessionStorage.setItem('friendRequestCount', count);
			
			// 通过事件总线通知好友页面更新
			this.$eventBus.$emit('updateFriendRequestCount', count);
			
			console.log(`Home.vue 更新好友申请数量: ${count}`);
		},
		
		// 格式化未读数量，当大于99时显示99+
		formatBadgeCount(count) {
			if (count > 99) {
				return '99+';
			}
			return count;
		},
		
		// 加载好友申请数量
		loadFriendRequestCount() {
			// 先从本地存储获取
			const storedCount = sessionStorage.getItem('friendRequestCount');
			if (storedCount !== null) {
				this.friendRequestCount = parseInt(storedCount);
			}
			
			// 然后从服务器获取最新数据
			this.checkFriendRequests();
		},
		
		onPageVisibilityChange() {
			if (document.visibilityState === 'visible') {
				this.checkFriendRequests();
			}
		},
		closeUserInfo() {
			this.$refs.userInfo.close();
		},
		onExit() {
			this.unloadStore();
			this.$wsApi.close(3000);
			sessionStorage.removeItem("accessToken");
			location.href = appRoot;
		},
		findMessageChat(chatInfo) {
			if (!chatInfo || !this.chatStore) {
				return null;
			}
			if (chatInfo.type === 'PRIVATE' && typeof this.chatStore.findChatByFriend === 'function') {
				return this.chatStore.findChatByFriend(chatInfo.targetId);
			}
			if (chatInfo.type === 'GROUP' && typeof this.chatStore.findChatByGroup === 'function') {
				return this.chatStore.findChatByGroup(chatInfo.targetId);
			}
			return typeof this.chatStore.findChat === 'function' ? this.chatStore.findChat(chatInfo) : null;
		},
		isChatDnd(chatInfo) {
			const chat = this.findMessageChat(chatInfo);
			return !!(chat && chat.dnd);
		},
		playAudioTip(chatInfo = null) {
			if (this.isChatDnd(chatInfo)) {
				console.log('会话已开启免打扰，跳过消息提示音');
				return;
			}
			// 离线消息不播放铃声
			if (this.chatStore.isLoading()) {
				return;
			}
			const saved = localStorage.getItem('messageSoundEnabled');
			if (saved === 'false') {
				return;
			}
			// 防止过于密集播放
			if (new Date().getTime() - this.lastPlayAudioTime > 1000) {
				this.lastPlayAudioTime = new Date().getTime();
				let audio = new Audio();
				const soundKey = localStorage.getItem(MESSAGE_SOUND_STORAGE_KEY) || DEFAULT_MESSAGE_SOUND_KEY;
				audio.src = getMessageSoundOption(soundKey).src;
				audio.play();
			}

		},
		showSetting() {
			this.showSettingDialog = true;
		},
		closeSetting() {
			this.showSettingDialog = false;
		},
		loadFriendInfo(id) {
			let friend = this.friendStore.findFriend(id);
			if (!friend) {
				friend = {
					id: id,
					showNickName: "未知用户",
					headImage: ""
				}
			}
			return friend;
		},
		loadGroupInfo(id) {
			let group = this.groupStore.findGroup(id);
			if (!group) {
				group = {
					id: id,
					showGroupName: "未知群聊",
					headImageThumb: ""
				}
			}
			return group;
		},
	},
	watch: {
		unreadCount: {
			handler(newCount, oldCount) {
				let tip = newCount > 0 ? `${newCount}条未读` : "";
				this.$elm.setTitleTip(tip);
			},
			immediate: true
		}
	},
	mounted() {
		this.init();
		this.$nextTick(() => this.flushPendingRtcPrivateMessages());
		// 初始化好友申请数量
		this.loadFriendRequestCount();
		document.addEventListener('visibilitychange', this.onPageVisibilityChange);
	},
	beforeDestroy() {
		this.$wsApi.close();
		document.removeEventListener('visibilitychange', this.onPageVisibilityChange);
		// 移除事件监听
		this.$eventBus.$off('updateFriendRequestCount');
	}
}
</script>

<style lang="scss">
/* 全局样式，不能加scoped */
.el-tooltip__popper.custom-tooltip {
  border: 1px solid #ff4d4f !important;
  border-radius: 2px;
  padding: 4px 8px;
  font-size: 12px !important;
  line-height: 1.2;
  color: #fff !important;
  background-color: #ff4d4f !important;
  max-width: 100px;
  white-space: nowrap;
}

.el-tooltip__popper.custom-tooltip[x-placement^=right-end] {
  margin-left: 0 !important;
  transform: translateX(0) !important;
  margin-right: 10px !important;
}

.el-tooltip__popper.custom-tooltip[x-placement^=right-end] .popper__arrow {
  display: none !important;
}

</style>

<style scoped lang="scss">
.home-page {
	height: 100vh;
	width: 100vw;
	display: flex;
	justify-content: center;
	align-items: center;
	border-radius: 4px;
	overflow: hidden;
	background: var(--im-color-primary-light-9);

	.app-container {
		width: 62vw;
		height: 80vh;
		display: flex;
		min-height: 600px;
		min-width: 970px;
		position: absolute;
		border-radius: 4px;
		overflow: hidden;
		box-shadow: var(--im-box-shadow-dark);
		transition: 0.2s;

		&.fullscreen {
			width: 100vw;
			height: 100vh;
		}
	}

	.navi-bar {
		// 图标字号和侧边栏宽度
		--icon-font-size: 22px;
		// 缩小宽度，大约为之前的一半
		--width: 130px;
		width: var(--width);
		// 略深的灰蓝色，让与中间区域区分更明显
		background: #e1e6f0;
		padding-top: 16px;

		.navi-bar-box {
			height: 100%;
			display: flex;
			flex-direction: column;
			justify-content: flex-start;
		}

		.user-head-image {
			display: flex;
			justify-content: center;
		}

		.menu {
			height: auto;
			margin-top: 16px;
			display: flex;
			flex-direction: column;
			align-content: stretch;

			.link {
				text-decoration: none;
			}

			.router-link-active .menu-item {
				color: #1976d2;
				background: #e3f2fd;
			}

			.link:not(.router-link-active) .menu-item:hover {
				color: #1976d2;
				background: #f0f4ff;
			}

			.menu-item {
				position: relative;
				color: #444;
				width: calc(var(--width) - 24px);
				height: 44px;
				display: flex;
				align-items: center;
				margin: 4px 12px;
				border-radius: 6px;
				padding: 0 10px;
				box-sizing: border-box;

				.icon {
					font-size: var(--icon-font-size);
					margin-right: 8px;
					flex-shrink: 0;
				}

				.nav-outline-icon {
					-webkit-text-fill-color: transparent;
					-webkit-text-stroke: 1.15px currentColor;
					font-weight: 400;
				}

				.label {
					font-size: 14px;
					white-space: nowrap;
				}

				.unread-text {
					position: absolute;
					background-color: var(--im-color-danger);
					left: 28px;
					top: 9px;
					color: #fff;
					border-radius: 30px;
					min-width: 14px;
					height: 14px;
					line-height: 14px;
					padding: 0 4px;
					font-size: 10px;
					text-align: center;
					white-space: nowrap;
					border: 1px solid #f1e5e5;
					box-sizing: border-box;
				}
			}
		}

		.bottom-item {
			// 与上方菜单项保持同样的外观
			position: relative;
			color: #444;
			width: calc(var(--width) - 24px);
			height: 44px;
			display: flex;
			align-items: center;
			margin: 4px 12px;
			border-radius: 6px;
			padding: 0 12px;
			box-sizing: border-box;
			cursor: pointer;
			font-size: var(--icon-font-size);

			.icon {
				font-size: var(--icon-font-size);
				margin-right: 8px;
			}

			.favorite-cube-outline-icon {
				flex: 0 0 auto;
				width: 1em;
				height: 1em;
				fill: none;
				stroke: currentColor;
				stroke-width: 1.8;
				stroke-linecap: round;
				stroke-linejoin: round;
			}

			.label {
				font-size: 14px;
			}

			&:hover {
				color: #1976d2;
				background: #f0f4ff;
			}
		}

		.bottom-exit {
			position: absolute;
			left: 12px;
			right: 12px;
			bottom: 12px;
		}
	}

	.content-box {
		flex: 1;
		padding: 0;
		background-color: #fff;
		text-align: center;
	}
}
</style>
